import { Bot, User } from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { WhatsAppDraftCard } from '../../agents/components/WhatsAppDraftCard';
import type { ProspectSearchMessage, ProspectSearchToolCall } from '../types';
import { redactUserFacingText } from '../utils/safety';
import { ChatMarkdown } from './ChatMarkdown';
import { ToolCard } from './ToolCard';

interface WhatsAppDraftOutput {
  draft: string;
  language: string;
  charCount: number;
}

function isWhatsAppDraftOutput(output: unknown): output is WhatsAppDraftOutput {
  return (
    typeof output === 'object' &&
    output !== null &&
    typeof (output as Record<string, unknown>).draft === 'string' &&
    typeof (output as Record<string, unknown>).language === 'string' &&
    typeof (output as Record<string, unknown>).charCount === 'number'
  );
}

function ToolCallCard({ toolCall }: { readonly toolCall: ProspectSearchToolCall }) {
  if (toolCall.name === 'draft_whatsapp_message' && !toolCall.error) {
    const output = toolCall.output;
    if (isWhatsAppDraftOutput(output)) {
      return (
        <WhatsAppDraftCard
          draft={output.draft}
          language={output.language}
          charCount={output.charCount}
        />
      );
    }
  }

  return <ToolCard toolCall={toolCall} />;
}

interface ChatMessageBubbleProps {
  readonly message: ProspectSearchMessage;
  readonly index: number;
}

export function ChatMessageBubble({ message, index }: ChatMessageBubbleProps) {
  const isUser = message.role === 'user';
  const safeContent = redactUserFacingText(message.content);
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(12)).current;
  const hasContent = Boolean(message.content?.trim());
  const hasToolCalls = Boolean(message.toolCalls && message.toolCalls.length > 0);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 350,
        delay: index * 60,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 350,
        delay: index * 60,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [opacity, translateY, index]);

  // The orchestrator persists intermediate tool-loop rows with empty
  // content and no toolCalls. If one ever reaches the client, don't
  // render it as a blank bubble (FLOSEAI-162).
  if (!hasContent && !hasToolCalls) {
    return null;
  }

  return (
    <Animated.View
      testID="chat-message-bubble"
      style={[
        styles.container,
        isUser ? styles.userContainer : styles.assistantContainer,
        { opacity, transform: [{ translateY }] },
      ]}
    >
      {!isUser && (
        <Box paddingTop="xs">
          <Box
            width={28}
            height={28}
            borderRadius="full"
            backgroundColor={colors.primary.muted}
            alignItems="center"
            justifyContent="center"
          >
            <Bot size={14} color={colors.primary.neon} strokeWidth={2} />
          </Box>
        </Box>
      )}

      <Box style={[styles.bubble, isUser ? styles.userBubble : styles.assistantBubble]}>
        {isUser ? (
          <Text variant="body" color={colors.text.primary} style={styles.messageText}>
            {safeContent}
          </Text>
        ) : (
          <ChatMarkdown content={safeContent} />
        )}

        {message.toolCalls?.map((toolCall) => (
          <ToolCallCard key={toolCall.id} toolCall={toolCall} />
        ))}
      </Box>

      {isUser && (
        <Box paddingTop="xs">
          <Box
            width={28}
            height={28}
            borderRadius="full"
            backgroundColor={colors.background.elevated}
            alignItems="center"
            justifyContent="center"
          >
            <User size={14} color={colors.text.primary} strokeWidth={2} />
          </Box>
        </Box>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    marginBottom: 12,
    maxWidth: '85%',
  },
  userContainer: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  assistantContainer: {
    alignSelf: 'flex-start',
  },
  bubble: {
    borderRadius: tokens.radius.lg,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexShrink: 1,
  },
  userBubble: {
    backgroundColor: colors.surface.row,
    borderBottomRightRadius: 4,
  },
  assistantBubble: {
    backgroundColor: colors.background.card,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
});
